import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar as CalendarIcon, Loader2, Upload, FileText, Image, File, Trash2, Download, Paperclip, Save } from 'lucide-react';
import { format } from 'date-fns';
import {
  fetchComplaintById,
  updateComplaint,
  fetchComplaintAttachments,
  addComplaintAttachment,
  deleteComplaintAttachment,
  uploadComplaintFile,
  deleteComplaintFile,
  getComplaintFileUrl,
  type ComplaintStatus,
  type ComplaintWithRelations,
  type ComplaintAttachment,
} from '@/core/api';
import { useAuth } from '@/core/AuthContext';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { CustomerSelector, ArticleSelector, HierarchySelector } from '@/shared/components/selectors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Separator } from '@/shared/ui/separator';
import { useToast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/lib/utils';
import { ComplaintStatusBadge } from './ComplaintStatusBadge';

interface ComplaintDetailDialogProps {
  complaintId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for viewing and editing a customer complaint with attachments.
 */
export const ComplaintDetailDialog = ({ complaintId, open, onOpenChange }: ComplaintDetailDialogProps) => {
  const { user } = useAuth();
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileLabel, setFileLabel] = useState('');

  // Fetch complaint data
  const { data: complaint, isLoading: isLoadingComplaint } = useQuery({
    queryKey: ['customer-complaint', complaintId],
    queryFn: () => fetchComplaintById(complaintId!),
    enabled: !!complaintId && open,
  });

  // Fetch attachments
  const { data: attachments, isLoading: isLoadingAttachments } = useQuery({
    queryKey: ['complaint-attachments', complaintId],
    queryFn: () => fetchComplaintAttachments(complaintId!),
    enabled: !!complaintId && open,
  });

  // Form state - initialize from complaint data
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [complaintDate, setComplaintDate] = useState<Date>(new Date());
  const [productionDate, setProductionDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<ComplaintStatus>('draft');
  const [complaintIdentifier, setComplaintIdentifier] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [comment, setComment] = useState('');
  const [areaId, setAreaId] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string | null>(null);
  const [stationId, setStationId] = useState<string | null>(null);
  const [articleId, setArticleId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Populate form when complaint data loads
  const populateForm = (c: ComplaintWithRelations) => {
    setCustomerId(c.customer_id);
    setComplaintDate(new Date(c.complaint_date));
    setProductionDate(c.production_date ? new Date(c.production_date) : null);
    setStatus(c.status);
    setComplaintIdentifier(c.complaint_identifier || '');
    setSerialNumber(c.serial_number || '');
    setComment(c.comment || '');
    setAreaId(c.area_id);
    setLineId(c.line_id);
    setStationId(c.station_id);
    setArticleId(c.article_id);
    setHasChanges(false);
  };

  // Populate form when dialog opens with new complaint
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && complaint) {
      populateForm(complaint);
    }
    onOpenChange(isOpen);
  };

  // Also populate when complaint data first loads
  if (complaint && !hasChanges && customerId !== complaint.customer_id) {
    populateForm(complaint);
  }

  // Track changes
  const markChanged = () => setHasChanges(true);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: () => updateComplaint(complaintId!, {
      customer_id: customerId!,
      complaint_date: format(complaintDate, 'yyyy-MM-dd'),
      production_date: productionDate ? format(productionDate, 'yyyy-MM-dd') : null,
      status,
      complaint_identifier: complaintIdentifier || null,
      serial_number: serialNumber || null,
      comment: comment || null,
      area_id: areaId,
      line_id: lineId,
      station_id: stationId,
      article_id: articleId,
    }),
    onSuccess: () => {
      toast({ title: 'Complaint updated' });
      queryClient.invalidateQueries({ queryKey: ['customer-complaints'] });
      queryClient.invalidateQueries({ queryKey: ['customer-complaint', complaintId] });
      queryClient.invalidateQueries({ queryKey: ['complaint-stats'] });
      setHasChanges(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update complaint',
        variant: 'destructive',
      });
    },
  });

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !complaintId || !selectedTenantId || !selectedFactoryId) return;

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'File too large (max 50MB)', variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${complaintId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      await uploadComplaintFile(filePath, file);
      await addComplaintAttachment(
        complaintId,
        selectedTenantId,
        selectedFactoryId,
        file.name,
        filePath,
        file.size,
        file.type,
        fileLabel.trim() || null,
        user?.id
      );

      queryClient.invalidateQueries({ queryKey: ['complaint-attachments', complaintId] });
      toast({ title: 'File uploaded' });
      setFileLabel('');
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Failed to upload file', variant: 'destructive' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Delete attachment mutation
  const deleteMutation = useMutation({
    mutationFn: async (attachment: { id: string; file_path: string }) => {
      try {
        await deleteComplaintFile(attachment.file_path);
      } catch (error) {
        console.error('Storage delete error:', error);
      }
      await deleteComplaintAttachment(attachment.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaint-attachments', complaintId] });
      toast({ title: 'File deleted' });
    },
    onError: () => {
      toast({ title: 'Failed to delete file', variant: 'destructive' });
    },
  });

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return <File className="h-5 w-5" />;
    if (fileType.startsWith('image/')) return <Image className="h-5 w-5 text-chart-2" />;
    if (fileType.includes('pdf')) return <FileText className="h-5 w-5 text-chart-3" />;
    return <File className="h-5 w-5 text-muted-foreground" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      toast({ title: 'Please select a customer', variant: 'destructive' });
      return;
    }
    updateMutation.mutate();
  };

  if (!complaintId) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle>Complaint Details</DialogTitle>
              <DialogDescription>
                View and edit complaint information, manage attachments
              </DialogDescription>
            </div>
            {complaint && <ComplaintStatusBadge status={complaint.status} />}
          </div>
        </DialogHeader>

        {isLoadingComplaint ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : complaint ? (
          <form onSubmit={handleSave} className="space-y-6">
            {/* Customer */}
            <div className="space-y-2">
              <Label>
                Customer <span className="text-destructive">*</span>
              </Label>
              <CustomerSelector
                value={customerId}
                onValueChange={(v) => { setCustomerId(v); markChanged(); }}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={(v) => { setStatus(v as ComplaintStatus); markChanged(); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="complaint_recorded">Complaint Recorded</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Complaint Date */}
              <div className="space-y-2">
                <Label>
                  Complaint Date <span className="text-destructive">*</span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start text-left font-normal')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(complaintDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={complaintDate}
                      onSelect={(date) => { if (date) setComplaintDate(date); markChanged(); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Production Date */}
              <div className="space-y-2">
                <Label>Production Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start text-left font-normal')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {productionDate ? format(productionDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={productionDate || undefined}
                      onSelect={(date) => { setProductionDate(date || null); markChanged(); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Article */}
            <div className="space-y-2">
              <Label>Article / Product</Label>
              <ArticleSelector
                value={articleId}
                onValueChange={(v) => { setArticleId(v); markChanged(); }}
                areaId={areaId}
                lineId={lineId}
                allowClear
              />
            </div>

            {/* Hierarchy */}
            <div className="space-y-2">
              <Label>Location</Label>
              <HierarchySelector
                areaId={areaId}
                lineId={lineId}
                stationId={stationId}
                onAreaChange={(v) => { setAreaId(v); markChanged(); }}
                onLineChange={(v) => { setLineId(v); markChanged(); }}
                onStationChange={(v) => { setStationId(v); markChanged(); }}
                includeStation
                allowClear
                showLabels={false}
              />
            </div>

            {/* Identifiers Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="complaint-identifier">Customer Reference</Label>
                <Input
                  id="complaint-identifier"
                  placeholder="e.g., BMW-2024-0123"
                  value={complaintIdentifier}
                  onChange={(e) => { setComplaintIdentifier(e.target.value); markChanged(); }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial-number">Serial Number</Label>
                <Input
                  id="serial-number"
                  placeholder="e.g., SN123456789"
                  value={serialNumber}
                  onChange={(e) => { setSerialNumber(e.target.value); markChanged(); }}
                />
              </div>
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="comment">Comment</Label>
              <Textarea
                id="comment"
                placeholder="Describe the complaint..."
                value={comment}
                onChange={(e) => { setComment(e.target.value); markChanged(); }}
                rows={3}
              />
            </div>

            {/* Save Button */}
            {hasChanges && (
              <div className="flex justify-end">
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}

            <Separator />

            {/* Attachments Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                <h3 className="font-semibold">Attachments</h3>
              </div>

              {/* Upload Section */}
              <div className="space-y-2">
                <Input
                  placeholder="Label (optional)"
                  value={fileLabel}
                  onChange={(e) => setFileLabel(e.target.value)}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {isUploading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </div>
              </div>

              {/* Files List */}
              {isLoadingAttachments ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : attachments && attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg border border-border"
                    >
                      {getFileIcon(attachment.file_type)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {attachment.label || attachment.file_name}
                        </div>
                        {attachment.label && (
                          <div className="text-xs text-muted-foreground truncate">
                            {attachment.file_name}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.file_size)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(getComplaintFileUrl(attachment.file_path), '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteMutation.mutate({ id: attachment.id, file_path: attachment.file_path })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4 border border-dashed rounded-lg">
                  No attachments yet
                </div>
              )}
            </div>

            {/* Created By Footer */}
            {complaint.created_by_profile && (
              <div className="text-xs text-muted-foreground pt-2">
                Created by {complaint.created_by_profile.full_name || complaint.created_by_profile.email}
                {' on '}
                {format(new Date(complaint.created_at), 'PPP')}
              </div>
            )}
          </form>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Complaint not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
